import { Camera, CircleDollarSign, Image, Pencil, Phone, Plus, Printer, RefreshCcw, SquareCheck, StickyNote, Trash2, Truck, Undo2, User, Voicemail } from "lucide-react";
import { useEffect, useState } from "react";
import { useAuth } from "react-oidc-context";
import { Link, useNavigate } from "react-router";
import { apiSend } from "../../api";
import { date, dateTime, money } from "../../format";
import { totals } from "../../pricing";
import type { Watch } from "../../types";
import { Card, Later } from "./Card";
import { Modal } from "./Modal";
import { useLabels } from "./useLabels";
import { useTicketContext } from "./useTicket";

export function ImagesCard() {
  const { detail } = useTicketContext();
  return (
    <Card title="Images" icon={Image} actions={<Later className="link-btn" icon={Camera} why="the webcam check-in">Open Camera</Later>}>
      <div className="img-placeholder">
        {detail.photos.length
          ? <>{detail.photos.length} photo{detail.photos.length === 1 ? "" : "s"} on file.<br /><span className="hint">They show here once copied from the old system.</span></>
          : "No photos yet."}
      </div>
    </Card>
  );
}

/** One watch's details, view or edit. */
function WatchRow({ w, ticketNo }: { w: Watch; ticketNo: number }) {
  const { save, saving, lookups } = useTicketContext();
  const [edit, setEdit] = useState(!w.brand && !w.model && !w.description);
  const [f, setF] = useState({ brand: w.brand ?? "", model: w.model ?? "", description: w.description ?? "" });
  useEffect(() => setF({ brand: w.brand ?? "", model: w.model ?? "", description: w.description ?? "" }), [w]);
  const label = `${ticketNo}-${w.position}`;
  if (!edit) {
    return (
      <li className="watch">
        <span className="watch-no">{label}</span>
        <span className="watch-text"><strong>{[w.brand, w.model].filter(Boolean).join(" ") || "Watch"}</strong>
          {w.description && <span className="hint"> · {w.description}</span>}</span>
        <button type="button" className="icon-link" aria-label={`Edit watch ${label}`} onClick={() => setEdit(true)}><Pencil size={15} /></button>
      </li>
    );
  }
  return (
    <li className="watch editing">
      <span className="watch-no">{label}</span>
      <form className="watch-form" onSubmit={async (e) => { e.preventDefault(); if (await save("PUT", `/watches/${w.id}`, f)) setEdit(false); }}>
        <input list="watch-brands" placeholder="Brand" value={f.brand} onChange={(e) => setF({ ...f, brand: e.target.value })} aria-label="Brand" autoFocus />
        <input placeholder="Model" value={f.model} onChange={(e) => setF({ ...f, model: e.target.value })} aria-label="Model" />
        <input className="wide" placeholder="Description (dial, case, condition)" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} aria-label="Description" />
        <datalist id="watch-brands">{lookups?.watchBrands.map((b) => <option key={b} value={b} />)}</datalist>
        <div className="btn-row tight">
          <button type="submit" className="btn primary" disabled={saving}>Save</button>
          {(w.brand || w.model || w.description) && <button type="button" className="btn outline" onClick={() => setEdit(false)}>Cancel</button>}
          <button type="button" className="icon-link danger" aria-label={`Remove watch ${label}`} disabled={saving}
            onClick={() => { if (window.confirm(`Remove watch ${label}? Its services stay on the estimate, no longer tied to a watch.`)) void save("DELETE", `/watches/${w.id}`); }}><Trash2 size={16} /></button>
        </div>
      </form>
    </li>
  );
}

export function CheckInCard() {
  const { detail, save, saving } = useTicketContext();
  const t = detail.ticket;
  const [desc, setDesc] = useState(t.description ?? "");
  useEffect(() => setDesc(t.description ?? ""), [t.description]);
  return (
    <Card title="Check-In" icon={SquareCheck}
      actions={<button type="button" className="icon-link" disabled title="Comes with the new check-in (photos and Zebra slips)" aria-label="Print check-in tag"><Printer size={18} /></button>}>
      <div className="ci-row"><span className="strong">Watches:</span><span>{detail.watches.length || t.productCount || 0}</span>
        {!detail.watches.length && t.brands && <span className="hint">({t.brands})</span>}</div>
      <ul className="watches">
        {detail.watches.map((w) => <WatchRow key={w.id} w={w} ticketNo={t.ticketNo} />)}
      </ul>
      <button type="button" className="link-btn" disabled={saving} onClick={() => void save("POST", "/watches", {})}><Plus size={15} />Add watch</button>
      <p className="hint">Soon: photograph each watch with the webcam and the details fill in for you.</p>
      <label className="field-lbl">Description
        <textarea rows={3} placeholder="What the customer reported, condition notes, special instructions…" value={desc} onChange={(e) => setDesc(e.target.value)} /></label>
      <div className="btn-row even">
        <button type="button" className="btn primary" disabled={saving || !!t.checkedInAt}
          title={t.checkedInAt ? `Checked in ${dateTime(t.checkedInAt)}` : "Moves the ticket to Pending Inspection; due date 7 days out"}
          onClick={() => { if (window.confirm("Check in this ticket? It moves to Pending Inspection with a due date 7 days out.")) void save("POST", "/check-in", { description: desc, productCount: detail.watches.length || t.productCount, brands: t.brands, expectedUpdatedAt: t.updatedAt }); }}>
          {t.checkedInAt ? "Checked In" : "Check In"}
        </button>
        <button type="button" className="btn outline" disabled={desc === (t.description ?? "") || saving}
          onClick={() => void save("PATCH", "", { description: desc, expectedUpdatedAt: t.updatedAt })}>Update</button>
      </div>
      {t.checkedInAt && <p className="hint center">Checked in {dateTime(t.checkedInAt)}</p>}
    </Card>
  );
}

const METHODS = [["check", "Check"], ["check_by_mail", "Check by Mail"], ["paypal", "PayPal"], ["venmo", "Venmo"], ["other", "Other"]] as const;

export function PaymentCard() {
  const { detail, lookups, save, saving } = useTicketContext();
  const t = detail.ticket;
  const [open, setOpen] = useState(false);
  const paid = detail.payments.filter((p) => p.succeeded).reduce((s, p) => s + (p.kind === "refund" ? -1 : 1) * Number(p.amount), 0);
  const taxed = !!detail.customer?.chargeSalesTax && (t.state ?? "").trim().toUpperCase() === "WI";
  const total = lookups ? totals(detail.items, Number(t.insuranceAmount ?? 0), taxed, lookups.pricing).total : 0;
  const balance = Math.max(0, Math.round((total - paid) * 100) / 100);
  const [f, setF] = useState({ method: "check", amount: "", note: "" });
  const ok = detail.payments.filter((p) => p.succeeded);
  return (
    <Card title="Payment" icon={CircleDollarSign}>
      <div className="pay-head"><span className="strong">Transaction Details</span>
        <span className="hint">{ok.length ? `${ok.length} transaction${ok.length === 1 ? "" : "s"} found` : "No transactions found"} · balance {money(balance)}</span></div>
      {detail.payments.length > 0 && (
        <table className="t-mini">
          <tbody>
            {detail.payments.map((p) => (
              <tr key={p.id} className={p.succeeded ? "" : "failed"} title={p.processorMessage ?? ""}>
                <td>{date(p.paidAt ?? p.createdAt)}</td>
                <td>{p.method.replace(/_/g, " ")}{p.kind === "refund" ? " refund" : ""}{p.succeeded ? "" : " · failed"}
                  {p.processorMessage && p.method !== "card" && <div className="hint">{p.processorMessage}</div>}</td>
                <td className="num">{money(p.amount)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <div className="btn-row wrap">
        <button type="button" className="btn outline" disabled={saving} onClick={() => { setF({ method: "check", amount: balance ? balance.toFixed(2) : "", note: "" }); setOpen(true); }}>
          <CircleDollarSign size={16} />Pay with Check</button>
        <Later icon={RefreshCcw} why="refunds through Authorize.Net">Refund</Later>
        <Later icon={User} why="Authorize.Net card on file">View Payment Profile</Later>
      </div>
      {open && (
        <Modal title="Record a payment" onClose={() => setOpen(false)}>
          <form className="mini-form" onSubmit={async (e) => { e.preventDefault(); if (await save("POST", "/payments", f)) setOpen(false); }}>
            <fieldset className="radios"><legend className="lbl">How did the customer pay?</legend>
              {METHODS.map(([v, l]) => (
                <label key={v}><input type="radio" name="method" checked={f.method === v} onChange={() => setF({ ...f, method: v })} /> {l}</label>
              ))}
            </fieldset>
            <label>Amount ($)<input type="number" min="0.01" step="0.01" required value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} autoFocus /></label>
            <label>Check number or note<input value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} /></label>
            <p className="hint">Balance due {money(balance)}. A payment that covers it moves the ticket to Ready to Ship.</p>
            <div className="btn-row"><button type="submit" className="btn primary" disabled={saving}>Record payment</button>
              <button type="button" className="btn outline" onClick={() => setOpen(false)}>Cancel</button></div>
          </form>
        </Modal>
      )}
    </Card>
  );
}

const TRACKING: Record<string, string> = { PRE_TRANSIT: "not scanned yet", TRANSIT: "in transit", DELIVERED: "delivered", RETURNED: "returned to sender", FAILURE: "delivery problem", UNKNOWN: "no tracking yet" };

/** Labels 2 (to the customer) and 3 (spare return label in the box): buy, print, reprint. */
export function ShippingLabelCard() {
  const { detail, saving } = useTicketContext();
  const { busy, print } = useLabels();
  const shown = detail.labels.filter((l) => l.purpose === "outbound" || l.purpose === "spare" || (!l.purpose && !l.toShop));
  const has = shown.some((l) => !l.voidedAt && !l.toShop);
  return (
    <Card title="Shipping Label" icon={Truck}>
      {shown.length > 0 && (
        <ul className="label-list">
          {shown.map((l) => (
            <li key={l.id} className={l.voidedAt ? "voided" : ""}>
              <strong>{l.toShop ? "Spare return label" : "To customer"}</strong> · {l.carrier} {l.serviceLevel}{l.signature ? " (signature)" : ""}
              {l.cost && <> · {money(l.cost)}</>}
              <div className="hint">
                {l.trackingNumber ?? "no tracking number"}
                {l.voidedAt ? ` · voided ${date(l.voidedAt)}${l.refundStatus ? `, refund ${l.refundStatus.toLowerCase()}` : ""}`
                  : l.trackingStatus ? ` · ${TRACKING[l.trackingStatus] ?? l.trackingStatus.toLowerCase()}` : ""}
              </div>
            </li>
          ))}
        </ul>
      )}
      <div className="btn-col narrow">
        <button type="button" className="btn outline" disabled={!!busy || saving} onClick={print}
          title={has ? "Reprint label 2 and the spare return label" : "Buy label 2 and the spare return label through Shippo, then print them"}>
          <Printer size={16} aria-hidden="true" /> {busy ?? "Print Shipping Labels"}</button>
      </div>
    </Card>
  );
}

export function ReturnsCard() {
  const { detail, config, reload } = useTicketContext();
  const auth = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const create = async () => {
    if (!window.confirm(`Create a return ticket for #${detail.ticket.ticketNo}? It starts at $0 with no services, status Returned, at WRP.`)) return;
    setBusy(true);
    try {
      const r = await apiSend<{ ticketNo: number }>(config, auth.user!.access_token, "POST", `/tickets/${detail.ticket.ticketNo}/return-ticket`);
      navigate(`/tickets/${r.ticketNo}`);
    } catch (e) {
      window.alert((e as Error).message);
      reload();
    } finally { setBusy(false); }
  };
  return (
    <Card title="Returns" icon={Undo2}>
      {detail.ticket.returnOfTicketNo && <p className="hint center">This is a return of <Link to={`/tickets/${detail.ticket.returnOfTicketNo}`}>#{detail.ticket.returnOfTicketNo}</Link></p>}
      {detail.returnTickets.length > 0 && <p className="hint center">Return tickets: {detail.returnTickets.map((n) => <Link key={n} to={`/tickets/${n}`}>#{n} </Link>)}</p>}
      <div className="btn-col narrow"><button type="button" className="btn outline" disabled={busy} onClick={() => void create()}>Create Return Ticket</button></div>
    </Card>
  );
}

const KINDS = [["note", "Note", StickyNote], ["phone_call", "Phone call", Phone], ["voicemail", "Voicemail", Voicemail]] as const;

/** Private notes (staff only), signed and dated, with status and location changes mixed in. */
export function NotesCard() {
  const { detail, save, saving } = useTicketContext();
  const [kind, setKind] = useState<"note" | "phone_call" | "voicemail">("note");
  const [body, setBody] = useState("");
  const entries = [
    ...detail.notes.map((n) => ({ at: n.createdAt, key: n.id, el: (
      <li key={n.id} className={`note ${n.kind}`}>
        <div className="meta"><strong>{KINDS.find((k) => k[0] === n.kind)?.[1] ?? "Note"}</strong> · {n.by ?? "Imported"} · {dateTime(n.createdAt)}</div>
        <div className="body">{n.body}</div>
      </li>) })),
    ...detail.history.map((h, i) => ({ at: h.at, key: `h${i}`, el: (
      <li key={`h${i}`} className="note activity-entry">
        <div className="meta">{h.kind === "status" ? "Status" : "Location"} · {h.by || "System"} · {dateTime(h.at)}</div>
        <div className="body">{h.from ?? "—"} → <strong>{h.to ?? "—"}</strong></div>
      </li>) })),
  ].sort((a, b) => b.at.localeCompare(a.at));
  return (
    <Card title="Private Notes" icon={StickyNote} actions={<span className="hint">Staff only</span>}>
      <form onSubmit={async (e) => { e.preventDefault(); if (await save("POST", "/notes", { kind, body })) setBody(""); }}>
        <div className="kind-tabs" role="radiogroup" aria-label="Kind of note">
          {KINDS.map(([v, l, Icon]) => (
            <label key={v} className={kind === v ? "on" : ""}><input type="radio" name="note-kind" checked={kind === v} onChange={() => setKind(v)} /><Icon size={14} />{l}</label>
          ))}
        </div>
        <textarea className="note-pad" rows={3} value={body} onChange={(e) => setBody(e.target.value)} aria-label="New note"
          placeholder={kind === "note" ? "Add a note…" : kind === "phone_call" ? "What was discussed?" : "What did you leave in the voicemail?"} />
        <div className="btn-row"><button type="submit" className="btn primary" disabled={!body.trim() || saving}>Add</button></div>
      </form>
      <ul className="notes-list">{entries.map((e) => e.el)}</ul>
    </Card>
  );
}
